import { isValidElement, useCallback, useRef } from 'react';
import type { ComponentType } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Button } from 'antd';
import cs from 'classnames';

import {
  selectShowSidebar,
  selectSidebarContentId
} from 'features/sidebar/sidebarSelectors';
import { closeSidebar } from 'features/sidebar/sidebarSlice';
import { selectTourState } from 'features/tour/tourSelectors';

import useHandleOutsideClick from 'hooks/useHandleOutsideClick';

import { SidebarContentTypes } from 'constants/enums';

import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';

import styles from './Sidebar.module.scss';
import componentMapping from './componentMapping';

const TOUR_TARGET_SELECTORS = [
  '[class*="__floater"]',
  '[class*="__tooltip"]',
  '[class*="__spotlight"]',
  '[class*="react-joyride"]'
];

const SIDEBAR_OUTSIDE_CLICK_IGNORED_CLASSES = [
  'table-card',
  'ant-table-row',
  'ant-select-clear'
];

const FILTERS_SIDEBAR_CONTENT_IDS = new Set<SidebarContentTypes>([
  SidebarContentTypes.JobsFilters,
  SidebarContentTypes.JobseekersFilters,
  SidebarContentTypes.ApplicationsFilters
]);

const Sidebar = () => {
  const dispatch = useDispatch();
  const isShowSidebar = useSelector(selectShowSidebar);
  const contentId = useSelector(selectSidebarContentId);
  const tourState = useSelector(selectTourState);

  const ref = useRef<HTMLDivElement>(null);

  const ContentComponent = contentId ? componentMapping[contentId] : null;
  const isFiltersSidebar = contentId
    ? FILTERS_SIDEBAR_CONTENT_IDS.has(contentId)
    : false;

  const handleClose = useCallback(
    (event?: MouseEvent) => {
      const isJobSidebar = contentId === SidebarContentTypes.ClientJobView;
      const isApplicationSidebar =
        contentId === SidebarContentTypes.ApplicationView;
      const isTourRunning = tourState.run && !tourState.isLoading;

      if ((isJobSidebar || isApplicationSidebar) && isTourRunning && event) {
        const target = event.target as HTMLElement;
        if (!target) return;

        const isTourElement = TOUR_TARGET_SELECTORS.some(
          (selector) => target.closest(selector) !== null
        );

        if (isTourElement) {
          return;
        }

        const isOutsideSidebar = ref.current && !ref.current.contains(target);
        if (isOutsideSidebar) {
          return;
        }
      }

      dispatch(closeSidebar());
    },
    [dispatch, contentId, tourState.run, tourState.isLoading, ref]
  );

  useHandleOutsideClick(
    ref,
    handleClose,
    isShowSidebar,
    SIDEBAR_OUTSIDE_CLICK_IGNORED_CLASSES
  );

  const handleCloseButtonClick = useCallback(() => {
    const isJobSidebar = contentId === SidebarContentTypes.ClientJobView;
    const isApplicationSidebar =
      contentId === SidebarContentTypes.ApplicationView;
    const isTourRunning = tourState.run && !tourState.isLoading;

    if ((isJobSidebar || isApplicationSidebar) && isTourRunning) {
      return;
    }

    dispatch(closeSidebar());
  }, [dispatch, contentId, tourState.run, tourState.isLoading]);

  return (
    <>
      <div
        className={cs('sidebar-container', styles.container, {
          [styles.show]: isShowSidebar,
          [styles.filters]: isFiltersSidebar
        })}
        ref={ref}
      >
        <Button className={styles.closeButton} onClick={handleCloseButtonClick}>
          <CloseIcon />
        </Button>
        {ContentComponent &&
          (isValidElement(ContentComponent)
            ? ContentComponent
            : (() => {
                const Component = ContentComponent as ComponentType;
                return <Component />;
              })())}
      </div>
    </>
  );
};

export default Sidebar;
